import { string, map, option } from "fp-ts";
import { pipe } from "fp-ts/lib/function";
import { Rubriek, RubriekReducer } from "./store/rubriekenReducer";
import { Value } from "./types";

const lookup = map.lookup(string.Eq);
const upsertAt = map.upsertAt(string.Eq);

const modifyAtWhenChanged = <K>(key: string, f: (k: K) => K) => (m: Map<string, K>) => {
  return pipe(
    m,
    lookup(key),
    option.map(f),
    // Upsert does a compare before setting the new value, this avoids mutating when it is not needed.
    option.map((newK) => pipe(m, upsertAt(key, newK)))
  );
};

const typedModifyAt = <A>() => <K extends keyof A, V extends A[K]>(key: K, value?: V) => (a: A): A => {
  if (a[key] === value) return a;

  return { ...a, [key]: value };
};

export const rubriekenFp = {
  lookup,
  lookupValue: <T extends Value>(code: string) => (rubrieken: RubriekReducer) =>
    pipe(
      rubrieken,
      lookup(code),
      option.chainNullableK((r) => r.value as T)
    ),
  modifyAt: modifyAtWhenChanged,
  upsertAt,
  filter: map.filter,
  filterWithIndex: map.filterWithIndex,
  isEmpty: map.isEmpty,
  keys: map.keys(string.Ord),
  map: map.map,
  toArray: map.toArray(string.Ord),
  Foldable: map.getFoldableWithIndex(string.Ord),
};

const rubriekFpModifyAt = typedModifyAt<Rubriek>();

export const rubriekFp = {
  modifyAt: rubriekFpModifyAt,
  clearValue: rubriekFpModifyAt("value", undefined),
  hasValue: (rubriek: Rubriek) => rubriek.value !== undefined && rubriek.value !== "",
  isInternalRubriek: (rubriek: Rubriek) => rubriek.internalRubriek !== undefined && rubriek.internalRubriek,
  isPartnerRubriek: (code: string, rubriek: Rubriek) =>
    code.startsWith("2") || code.startsWith("4") || rubriek.mar === "1",
};
